import clsx from 'clsx'
import { Button } from 'primereact/button'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { InputText } from 'primereact/inputtext'
import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAuthContext } from '../../context/AuthContext'
import { useToast } from '../../context/toast/ui'
import { ResursTreeApi } from '../../services/modules/tree'
import { clearCurrentProps, clearSelectNodes } from '../../services/slices/tree'
import { RootState, useAppDispatch } from '../../services/types/store-types'
import { Field } from './field/field'
import { TProprties } from './field/types'
import styles from './resurs-props.module.css'

export const ResursProps: FC<{
	show: boolean
	setShow?: (arg: boolean) => void
}> = ({ show, setShow }) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [currentProps, setCurrentProps] = useState<TProprties | null>(null)
	const lastSelect = useSelector((state: RootState) => state.tree.selectedNodes)
	const [initialValues, setInitialValues] = React.useState<any>()
	const changedField = React.useRef('')
	const dispatch = useAppDispatch()
	const lastRequestProps = useSelector(
		(state: RootState) => state.tree.currentProps
	)
	useEffect(() => {
		if (
			!lastSelect.objectIds ||
			!lastSelect.tree ||
			lastSelect.objectIds[0] === 'root'
		) {
			dispatch(clearCurrentProps())
			setCurrentProps(null)
			setInitialValues({})
			return
		}

		if (!lastRequestProps || typeof lastRequestProps === 'boolean') {
			setCurrentProps(null)
			setInitialValues({})
			return
		}

		setCurrentProps(lastRequestProps.properties)
		const data = propsMap(lastRequestProps.properties)
		data && setInitialValues(data)

		setSearchTerm('')
	}, [lastRequestProps, lastSelect])

	useEffect(() => {
		if (!lastRequestProps || typeof lastRequestProps !== 'object') return
		if (searchTerm) {
			const filterProps = lastRequestProps.properties.filter(prop =>
				prop.templateOptions.label
					.toLowerCase()
					.includes(searchTerm.toLowerCase())
			)
			setCurrentProps(filterProps)
		} else {
			setCurrentProps(lastRequestProps.properties)
		}
	}, [lastRequestProps, searchTerm])

	// TODO: Вынес в утилки функцию getDefaultProperties
	// Она в общем случае работает со структурой данных property
	// TODO: Переписать!
	const propsMap = (currentProps: TProprties) => {
		if (currentProps) {
			return currentProps.reduce((acc: any, currentObj) => {
				const key = currentObj.key
				if (currentObj.type === 'multi-select') {
					acc[key] = currentObj.templateOptions.options?.filter(
						opt => opt.selected
					)
				} else {
					const value = currentObj.defaultValue
					acc[key] = value
				}

				return acc
			}, {})
		}
	}

	const { register, setValue, control, reset } = useForm({
		reValidateMode: 'onChange',
		defaultValues: initialValues,
	})

	const { userRole } = useAuthContext()
	const { showMessage } = useToast()

	const handleChange = async (fieldName: string, data?: any) => {
		if (userRole === 'usr') {
			showMessage({
				caption: 'Ошибка',
				message: 'У вас нет прав!',
				tag: 'add',
				timeOut: 3,
				type: 'error',
			})

			return
		}

		changedField.current = fieldName
		if (lastSelect.tree && lastSelect.objectIds) {
			const updateData: Record<string, any> = {}
			updateData[fieldName] = data
			const newProps = await ResursTreeApi.setPropertys({
				tree: lastSelect.tree,
				objectId: lastSelect.objectIds,
				data: updateData,
			})

			if (typeof newProps === 'object' && newProps !== null) {
				if (
					typeof newProps === 'object' &&
					newProps &&
					'properties' in newProps
				) {
					setCurrentProps(newProps.properties)
					const data = propsMap(newProps.properties)
					data && setInitialValues(data)
				}
			}
		}
	}

	const handleClose = () => {
		setShow && setShow(false)
	}

	useEffect(() => {
		if (initialValues) {
			reset(initialValues)
		}
	}, [initialValues, currentProps, reset])

	const location = useLocation()

	useEffect(() => {
		if (location.pathname === '/config') {
			dispatch(clearCurrentProps())
			dispatch(clearSelectNodes())
			setCurrentProps(null)
		}
	}, [dispatch, location])

	return (
		<div
			className={clsx(
				styles.resursProps,
				show ? styles.expanded : styles.collapse
			)}
		>
			<div className={styles.header}>
				{show && <div className={styles.title}>Свойства</div>}
				<Button
					icon={`pi ${show ? 'pi-angle-double-left' : 'pi-cog'}`}
					style={{
						marginLeft: 'auto',
						height: 35,
						maxWidth: 35,
						width: '100%',
						border: '1px solid #E2E8F0',
						fontWeight: 500,
						backgroundColor: show ? '' : '#F1F5F9',
					}}
					outlined
					severity='secondary'
					onClick={handleClose}
				/>
			</div>

			{show && lastSelect.nodes.length > 0 && (
				<>
					{/** * Блок фильтрации свойств */}
					<div className={styles.searchBar}>
						<IconField iconPosition='left'>
							<InputIcon className='pi pi-search'> </InputIcon>
							<InputText
								placeholder='Поиск'
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
							/>
						</IconField>
					</div>
					{/** * Название выбранного/выбранных объектов */}
					<div className={styles.label}>
						{lastSelect.nodes.length === 1
							? lastSelect.nodes[0].name
							: `Общие свойства ${lastSelect.nodes.length} объектов`}
					</div>
					{/** * Форма свойств */}
					<form className={styles.form}>
						{currentProps &&
							currentProps.map((prop, idx) => (
								<Field
									key={prop.key}
									uid={idx}
									reg={register}
									change={handleChange}
									prop={prop}
									control={control}
									set={setValue}
								/>
							))}
					</form>
				</>
			)}
		</div>
	)
}
